/**
 * What a link inside a Copilot (beta) answer should do. ACOP-152 test finding, AC-12.
 *
 * Deliberately free of React so the rule can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/sourceLabels.ts.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 *
 * The answering model is told to cite with `[1]`, `[2]` (AVS OS rag-search-service.ts) and those
 * become the numbered pills. But when an answer ends "refer to these articles", the model sometimes
 * writes the list as markdown links whose target is only the source number:
 * `[Working with Outbound Shipments](#4)`. Captured on copilot-stgn 2026-10-27 for "How do I ship a
 * sales order?": six such links, each link's text exactly source N's title.
 *
 * Rendered as a plain `<a href="#4">` the browser resolves that against the SPA page —
 * `https://copilot-stgn.avsight.net/copilot-2#4` — and the click handler, which sends every link to
 * the embed page, opened an embed of `#4`. Neither does anything useful.
 *
 * The number is unambiguous: it is the same 1-based index the pills use, into the same `sources`
 * list. So the link is treated as a citation of that source and opens it in the same panel.
 *
 * ── Why the UI copes rather than AVS OS being asked to stop ────────────────────────────────────
 *
 * A model instruction is not a guarantee, and AVS OS is another repo. Whether to also change the
 * prompt is recorded for AVS OS triage (PRD Out-of-scope). This handles what the model actually writes.
 */

export type AnswerSource = {
  id?: string;
  title?: string;
  citationUrl?: string | null;
};

export type AnswerLinkTarget =
  | { kind: "citation"; source: AnswerSource }
  | { kind: "text" }
  | { kind: "link"; href: string | undefined };

/** `#` followed by digits and nothing else — the model's "link to source N". */
const SOURCE_NUMBER = /^#(\d+)$/;

/**
 * - `#N` with source N having something to open → `citation` (open it in the panel).
 * - `#N` with no such source, a source with nothing to open (the numbered pill is greyed out for
 *   those too), or any other fragment-only target (`#`, `#intro`) → `text`: it has nowhere to go
 *   inside this app, so it must not look like a link.
 * - everything else, including a missing target → `link`, exactly as before.
 */
export function resolveAnswerLink(
  href: string | undefined,
  sources: AnswerSource[] | undefined,
): AnswerLinkTarget {
  if (typeof href !== "string" || !href.startsWith("#")) return { kind: "link", href };
  const match = SOURCE_NUMBER.exec(href);
  const source = match ? sources?.[parseInt(match[1], 10) - 1] : undefined;
  return source?.citationUrl ? { kind: "citation", source } : { kind: "text" };
}

/**
 * ── The title written in front of its own number (ACOP-152 follow-up) ───────────────────────────
 *
 * AVS OS now tells the model to name an article as its title in plain text followed by its number,
 * "Working with Outbound Shipments [4]", and never to write a link (rag-search-service.ts; the links it used
 * to write were `#N` ones that opened nothing, or invented web addresses). The number becomes the
 * clickable pill, but the title beside it would be plain text, and an answer with no link in it reads as
 * one that cannot be followed up.
 *
 * The title is the source's own title, so the UI can make it the link. `text` is the plain text that ends
 * just before a `[n]` marker. When what it ends with is source n's title (compared without regard to
 * case, starting at a word boundary, with only whitespace between it and the marker), return the text in
 * front of the title, the title as the answer wrote it, and that whitespace. Nothing else is touched: a
 * title in the middle of a sentence, another source's title, a source with no page to open.
 */
export type TitleBeforeRef = { before: string; title: string; gap: string };

const WORD_CHAR = /[\p{L}\p{N}_]/u;

export function splitTitleBeforeRef(
  text: string,
  n: number,
  sources: AnswerSource[] | undefined,
): TitleBeforeRef | null {
  const source = sources?.[n - 1];
  const title = source?.title?.trim();
  if (!source?.citationUrl || !title) return null;

  const gap = /\s*$/.exec(text)?.[0] ?? "";
  const head = text.slice(0, text.length - gap.length);
  if (head.length < title.length) return null;

  const tail = head.slice(head.length - title.length);
  if (tail.toLowerCase() !== title.toLowerCase()) return null;

  const before = head.slice(0, head.length - title.length);
  if (before && WORD_CHAR.test(before[before.length - 1])) return null;
  return { before, title: tail, gap };
}

/**
 * ── The same, when the answer writes the title in bold: `**Outbound QC** [10]` ─────────────────
 *
 * Then the thing in front of the marker is a rendered element (a <strong>), not text, so splitTitleBeforeRef
 * never sees the title. Seen on copilot-stgn for copilot-test-employee, 2026-10-28: all three titles in the
 * answer were bold and none was a link. Whether the model bolds its titles varies from answer to answer.
 *
 * The rule is the same, applied to an element's own text instead of the tail of a string: the element's text
 * is exactly source n's title (case and surrounding whitespace aside) and the next thing in the line is `[n]`.
 */

/** Is `text` exactly source n's title, and does source n have a page to open? */
export function isSourceTitle(text: string, n: number, sources: AnswerSource[] | undefined): boolean {
  const source = sources?.[n - 1];
  const title = source?.title?.trim();
  if (!source?.citationUrl || !title) return false;
  return text.trim().toLowerCase() === title.toLowerCase();
}

/** `n` when `text` starts (after any whitespace) with a `[n]` marker, else null. */
export function leadingRefNumber(text: string | undefined): number | null {
  const m = typeof text === "string" ? /^\s*\[(\d+)\]/.exec(text) : null;
  return m ? parseInt(m[1], 10) : null;
}

/**
 * The text a piece of rendered markdown shows: a string is itself, an element is its children's text. Takes
 * React nodes but does not import React, so the rule above can be unit-tested directly.
 */
export function plainTextOf(node: unknown): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(plainTextOf).join("");
  const children = (node as { props?: { children?: unknown } } | null | undefined)?.props?.children;
  return children === undefined ? "" : plainTextOf(children);
}
